import React from 'react';
import { TrendingUp, Target, Shield, DollarSign, Plus } from 'lucide-react';
import { holidayMethodDescriptions } from '../utils/loadDescriptions';
import { SurchargeMethodSelector as SharedSurchargeMethodSelector, type SurchargeMethod } from '../../shared/surcharge/SurchargeMethodSelector';
import type { RateMultiplier } from '@/types';

interface HolidayPremiumMethodSelectorProps {
  holidayPremiumType: 'offset_overtime' | 'fixed_dollar' | 'hourly_premium' | 'guaranteed_rate';
  onHolidayPremiumTypeChange: (value: 'offset_overtime' | 'fixed_dollar' | 'hourly_premium' | 'guaranteed_rate') => void;
  allNonStraightTimeMultipliers: RateMultiplier[];
  customizedFields?: Record<string, boolean>;
  context?: 'user' | 'client';
  markdownVariables: Record<string, unknown>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const holidayPremiumMethods: SurchargeMethod[] = [
  {
    value: 'offset_overtime',
    label: 'Offset Overtime',
    description: 'Every hour of the holiday shift is paid one overtime tier higher than it normally would be.',
    icon: TrendingUp,
    buttonLabel: 'Offset Overtime',
    technicalName: 'Offset Overtime'
  },
  {
    value: 'guaranteed_rate',
    label: 'Guaranteed Rate',
    description: 'Your pay is brought up to a chosen rate tier for holiday hours. If you already earn that rate or higher from overtime, no additional surcharge is added.',
    icon: Shield,
    buttonLabel: 'Guaranteed Rate',
    technicalName: 'Guaranteed Rate'
  },
  {
    value: 'fixed_dollar',
    label: 'Fixed Premium',
    description: 'Adds a flat dollar amount to the shift total, regardless of hours worked or rate.',
    icon: DollarSign,
    technicalName: 'Fixed Premium'
  },
  {
    value: 'hourly_premium',
    label: 'Hourly Premium',
    description: 'Adds a per-hour premium on top of your base rate for the duration of the holiday shift.',
    icon: Plus
  }
];

export const HolidayPremiumMethodSelector: React.FC<HolidayPremiumMethodSelectorProps> = ({
  holidayPremiumType,
  onHolidayPremiumTypeChange,
  allNonStraightTimeMultipliers,
  customizedFields = {},
  context = 'user',
  markdownVariables
}) => {
  const isMethodDisabled = (method: SurchargeMethod): boolean => {
    const noMultipliers = allNonStraightTimeMultipliers.length === 0;
    return (method.value === 'offset_overtime' || method.value === 'guaranteed_rate') && noMultipliers;
  };

  const getDisabledTooltip = (): string => {
    return "Add at least one additional rate multiplier to enable this method.";
  };

  return (
    <div>
      <SharedSurchargeMethodSelector
        id="holiday_premium_type"
        title="Holiday Premium Method"
        methods={holidayPremiumMethods}
        selectedValue={holidayPremiumType}
        onValueChange={(value) => onHolidayPremiumTypeChange(value as HolidayPremiumMethodSelectorProps['holidayPremiumType'])}
        methodDescriptions={holidayMethodDescriptions}
        isMethodDisabled={isMethodDisabled}
        getDisabledTooltip={getDisabledTooltip}
        markdownVariables={markdownVariables}
        columns={4}
        isCustomized={customizedFields.holiday_premium_type}
        context={context}
      />
    </div>
  );
};
